import type { UserProfile } from '@/entities/auth';

export const IS_DEMO_APP_MODE = import.meta.env.MODE === 'demo';

/** 데모 로그인 유지(sessionStorage — 탭 종료 전까지 유지됨; 브라우저 탭 간 공유되지 않음) */
export const DEMO_SESSION_STORAGE_KEY = 'cyclone_demo_auth_profile_v1';

const ADMIN_LOGIN_ID = 'admin';
/** admin 계정 로그인 시에만 검증되는 비밀번호 */
export const DEMO_ADMIN_PASSWORD = 'onepredict1!';

export const ADMIN_DEMO_PROFILE: UserProfile = {
  username: 'admin',
  email: 'yoonyoung.choi@onepredict.com',
  firstName: '윤영',
  lastName: '최',
  roles: ['admin'],
};

export const MANAGER_DEMO_PROFILE: UserProfile = {
  username: 'manager',
  email: 'manager.demo@onepredict.com',
  firstName: '매니저',
  lastName: '데모',
  roles: ['manager'],
};

/**
 * 데모 로그인 규격
 * - `admin`: 비밀번호가 `DEMO_ADMIN_PASSWORD`와 일치해야 함
 * - `manager`(아이디만): manager 권한
 * - 그 외 모든 아이디(비밀번호 없음): user 권한
 */
export const tryDemoCredentialLogin = (username: string, password: string | undefined): UserProfile | null => {
  const key = username.trim().toLowerCase();
  if (!key) {
    return null;
  }

  if (key === ADMIN_LOGIN_ID) {
    if ((password ?? '') !== DEMO_ADMIN_PASSWORD) {
      return null;
    }
    return {
      username: ADMIN_DEMO_PROFILE.username,
      email: ADMIN_DEMO_PROFILE.email,
      firstName: ADMIN_DEMO_PROFILE.firstName,
      lastName: ADMIN_DEMO_PROFILE.lastName,
      roles: [...ADMIN_DEMO_PROFILE.roles],
    };
  }

  if (key === 'manager') {
    return {
      username: MANAGER_DEMO_PROFILE.username,
      email: MANAGER_DEMO_PROFILE.email,
      firstName: MANAGER_DEMO_PROFILE.firstName,
      lastName: MANAGER_DEMO_PROFILE.lastName,
      roles: [...MANAGER_DEMO_PROFILE.roles],
    };
  }

  const displayName = username.trim();
  const safeSlug = displayName.replace(/\s+/g, '');
  if (!safeSlug) {
    return null;
  }

  return {
    username: safeSlug,
    email: `${safeSlug.toLowerCase().replace(/[^a-zA-Z0-9._+-]/g, '_')}@demo.local`,
    firstName: safeSlug,
    lastName: '',
    roles: ['user'],
  };
};

export const isValidDemoPersistedProfile = (data: unknown): data is UserProfile => {
  if (!data || typeof data !== 'object') return false;

  const o = data as Record<string, unknown>;

  return (
    typeof o.username === 'string' &&
    typeof o.email === 'string' &&
    Array.isArray(o.roles) &&
    o.roles.every((r) => typeof r === 'string')
  );
};

export const persistDemoAuthSession = (user: UserProfile): void => {
  sessionStorage.setItem(DEMO_SESSION_STORAGE_KEY, JSON.stringify(user));
};

export const clearDemoAuthSession = (): void => {
  sessionStorage.removeItem(DEMO_SESSION_STORAGE_KEY);
};

export const readPersistedDemoUser = (): UserProfile | null => {
  try {
    const raw = sessionStorage.getItem(DEMO_SESSION_STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);

    return isValidDemoPersistedProfile(parsed) ? parsed : null;
  } catch {
    return null;
  }
};
